import { useRef, useEffect, useState } from "react";
// @ts-ignore - intl-tel-input v19 has no TypeScript declarations
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import utilsUrl from "intl-tel-input/build/js/utils.js?url";
import Inputmask from "inputmask";

interface Props {
  id?: string;
  onChangeNumber: (number: string) => void;
  onChangeValidity: (isValid: boolean) => void;
  onChangeIsVoip?: (isVoip: boolean) => void;
  /** Fires with the selected/detected country's ISO2 (e.g. "us", "gb") on input or country change. */
  onChangeCountry?: (iso2: string) => void;
  hasError?: boolean;
  ariaLabel?: string;
  countryCodeAriaLabel?: string;
  className?: string;
  disabled?: boolean;
}

// Mirrors idme-verification phone.js.coffee applyMask():
//   US  → example digits replaced by "9" e.g. "(999) 999-9999"
//   Other → "9{0,max}" where max is the digit count of the example mobile number
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildMask(iti: any): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const utils = (window as any).intlTelInputUtils;
  const country = iti.getSelectedCountryData();
  const iso2: string = country?.iso2 ?? "us";
  const example: string = utils.getExampleNumber(iso2, true, utils.numberType.MOBILE);

  // US and US territories share the NANP 10-digit format (+1), so use the grouped
  // US-style mask for any +1 country. This also keeps the mask stable when typing
  // auto-switches the flag between the US and a territory (e.g. Puerto Rico).
  if (iso2 === "us" || country?.dialCode === "1") {
    return example.replace(/\d/g, "9");
  }

  const maxDigits = example.replace(/\D/g, "").length;
  return `9{0,${maxDigits}}`;
}

export function IntlTelInput({ id, onChangeNumber, onChangeValidity, onChangeIsVoip, onChangeCountry, hasError, ariaLabel, countryCodeAriaLabel = "Country code", className, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itiRef = useRef<any>(null);
  const maskRef = useRef<string>("");
  // Guards the re-entrant countrychange our own setNumber() resync fires.
  const resyncingRef = useRef(false);
  // Caret position captured in keydown, used in onInput to restore after format
  const caretRef = useRef<number>(0);
  const onChangeNumberRef = useRef(onChangeNumber);
  const onChangeValidityRef = useRef(onChangeValidity);
  const onChangeIsVoipRef = useRef(onChangeIsVoip);
  const onChangeCountryRef = useRef(onChangeCountry);
  const [placeholder, setPlaceholder] = useState("(___) ___-____");

  onChangeNumberRef.current = onChangeNumber;
  onChangeValidityRef.current = onChangeValidity;
  onChangeIsVoipRef.current = onChangeIsVoip;
  onChangeCountryRef.current = onChangeCountry;

  const updateMask = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window as any).intlTelInputUtils || !itiRef.current) return;
    const mask = buildMask(itiRef.current);
    maskRef.current = mask;
    // Derive placeholder: "9" → "_", strip quantifier syntax for display
    setPlaceholder(mask.replace(/9/g, "_").replace(/\{[^}]+\}/g, ""));
  };

  const fireCallbacks = () => {
    if (!itiRef.current) return;
    const country = itiRef.current.getSelectedCountryData();
    const num = itiRef.current.getNumber() as string;
    const iso2 = (country?.iso2 as string) ?? "";
    let valid = itiRef.current.isValidNumber() as boolean;
    // For NANP (+1), require 10 actually-typed digits. intl-tel-input auto-prepends a
    // territory area code (e.g. American Samoa 684), so getNumber() reconstructs a
    // "valid" 10-digit number from a 7-digit entry; gate on the visible digit count.
    if (valid && country?.dialCode === "1") {
      const typedDigits = (inputRef.current?.value ?? "").replace(/\D/g, "").length;
      if (typedDigits < 10) valid = false;
    }
    onChangeNumberRef.current(num);
    onChangeValidityRef.current(valid);
    if (onChangeIsVoipRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const utils = (window as any).intlTelInputUtils;
      if (utils) {
        const numberType = itiRef.current.getNumberType() as number;
        onChangeIsVoipRef.current(numberType === utils.numberType.VOIP);
      }
    }
    onChangeCountryRef.current?.(iso2);
  };

  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;

    itiRef.current = intlTelInput(inputEl, {
      initialCountry: "us",
      nationalMode: true,
      formatOnDisplay: true,
      autoPlaceholder: "off",
      preferredCountries: ["us", "gb", "de", "kr", "ca", "it", "au", "nz"],
      excludeCountries: ["er", "pk"],
      dropdownContainer: document.body,
      utilsScript: utilsUrl,
      i18n: { selectedCountryAriaLabel: countryCodeAriaLabel },
    });

    const handleCountryChange = () => {
      updateMask();

      // Re-derive the country from the number so a manual dropdown pick can't mislabel
      // it — e.g. forcing "US" on a Canadian +1 number (604…), which shares the US dial
      // code and would otherwise validate as US. setNumber() re-runs area-code detection
      // and snaps the flag back to the true region; it re-fires countrychange, hence the
      // guard. Feed the E.164 value, falling back to the raw input (detection needs no utils).
      const iti = itiRef.current;
      if (iti && inputEl && !resyncingRef.current) {
        const current = (iti.getNumber() as string) || inputEl.value;
        if (current) {
          resyncingRef.current = true;
          iti.setNumber(current);
          resyncingRef.current = false;
        }
      }

      // Reformat the (possibly re-detected) digits under the new mask instead of
      // clearing — clearing mid-entry (e.g. 787 → Puerto Rico) would trap the user
      // in a detect → clear → re-detect loop.
      if (inputEl && maskRef.current) {
        inputEl.value = Inputmask.format(inputEl.value, { mask: maskRef.current });
      }
      fireCallbacks();
    };

    inputEl.addEventListener("countrychange", handleCountryChange);

    const pollId = setInterval(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).intlTelInputUtils) {
        clearInterval(pollId);
        updateMask();
      }
    }, 50);

    return () => {
      clearInterval(pollId);
      inputEl.removeEventListener("countrychange", handleCountryChange);
      itiRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirrors idme-verification phone-input/index.ts setHiddenInputValue():
  // format with im.format() then manually restore caret — no .mask() attachment.
  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const inputEl = inputRef.current;
    if (!inputEl || !itiRef.current) return;

    const mask = maskRef.current;
    if (mask) {
      const formatted = Inputmask.format(inputEl.value, { mask });
      inputEl.value = formatted;

      const inputType = (e.nativeEvent as InputEvent).inputType;
      if (inputType === "deleteContentBackward") {
        inputEl.setSelectionRange(caretRef.current - 1, caretRef.current - 1);
      } else if (inputType === "deleteContentForward") {
        inputEl.setSelectionRange(caretRef.current, caretRef.current);
      } else {
        // Move caret to next unfilled position ("_"), or end if complete
        const nextPos = formatted.indexOf("_", caretRef.current);
        if (nextPos !== -1) {
          inputEl.setSelectionRange(nextPos, nextPos);
        } else {
          inputEl.setSelectionRange(formatted.length, formatted.length);
        }
      }
    }

    fireCallbacks();
  };

  // Store caret position before the input value changes (mirrors handleKeyDown in phone-input)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    if (el.selectionStart !== null) {
      caretRef.current = el.selectionStart;
    }
  };

  // On focus, move caret to first unfilled "_" position (mirrors handleFocus in phone-input)
  const handleFocus = () => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const nextPos = inputEl.value.indexOf("_", 0);
    if (nextPos !== -1) {
      setTimeout(() => {
        inputEl.setSelectionRange(nextPos, nextPos);
      }, 0);
    }
  };

  return (
    <input
      id={id}
      type="tel"
      ref={inputRef}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      className={className}
      placeholder={placeholder}
      autoComplete="tel"
      disabled={disabled}
      aria-required="true"
      aria-label={ariaLabel ?? "Phone number"}
      aria-invalid={hasError}
      data-error={hasError ? "true" : undefined}
    />
  );
}
